import React, { useCallback } from 'react';
import {
  useActiveTab,
  useSubFormApproval,
  useHcpLicensesListApproval,
  useCurrentStageStatus,
} from '../../../../../hooks';
import { FormSectionsLayout, tabIdMatch } from './SharedComponents';
import axiosInstance from '../../../../../axiosInstance';
import HcpProfileSectionReviewerPanel from '../../HcpProfileSection/HcpProfileSectionReviewerPanel';
import { getCommentDedupeKey } from '../../../../../utils/commentKeys';

export const FormSectionsCustomer = ({
  subForms,
  requestProgressId,
  prevComments,
  requestDetails,
  filledForm,
  requestId,
  handleAddComment,
  requestProgress,
  children,
  handleAction,
  isReadOnly,
  // Additional props for advanced use cases
  editable = false,
  onFormDataChange = null,
  syncFormSectionsData = null,
  skipApprovalFetch = false,
  errors = null,
  activeTab: externalActiveTab = null,
  onTabChange: externalOnTabChange = null,
  showRequestBadges = true,
  enableAddComment: enableAddCommentProp = true,
  /** Profile subforms only: allow add when sendback/resubmit or while submitted (under review). */
  enableProfileSectionAddComment = null,
  formName = null,
  hasHcpLicensesList = false,
  isSendbackMode = false,
  setComments = () => {},
  loadingComments = false,
  registerSendbackBaseline = () => {},
  hcpProfileMenuEnabled = false,
  hcpProfileMainMenuTabs = null,
  renderHcpProfileMenuContent = null,
}) => {
  const [hcpLicensesListComments, setHcpLicensesListComments] = React.useState(
    []
  );
  const [hcpCommentsLoaded, setHcpCommentsLoaded] = React.useState(false);
  const [commentsRefetchKey, setCommentsRefetchKey] = React.useState(0);
  const [profileSectionComments, setProfileSectionComments] = React.useState(
    []
  );
  const [approvedProfileSectionKeys, setApprovedProfileSectionKeys] =
    React.useState([]);
  const [profileExtrasLoaded, setProfileExtrasLoaded] = React.useState(false);

  const hcpListRow = requestDetails?.hcpLicensesList;

  const { isApproved: isHcpLicensesListApproved } = useHcpLicensesListApproval({
    hcpLicensesListId: hcpListRow?.id,
    requestId: requestId,
    persona: 'enduser',
  });

  React.useEffect(() => {
    let cancelled = false;

    const fetchHcpComments = async () => {
      if (!hcpListRow?.id) {
        if (!cancelled) {
          setHcpLicensesListComments([]);
          setHcpCommentsLoaded(true);
        }
        return;
      }
      if (!cancelled) setHcpCommentsLoaded(false);
      try {
        const response = await axiosInstance.get(
          `/api/hcp-licenses-list-comments/enduser/${hcpListRow.id}`
        );
        if (!cancelled) {
          setHcpLicensesListComments(response.data || []);
          setHcpCommentsLoaded(true);
        }
      } catch (error) {
        console.error('Error fetching License Information comments:', error);
        if (!cancelled) {
          setHcpLicensesListComments([]);
          setHcpCommentsLoaded(true);
        }
      }
    };

    fetchHcpComments();
    return () => {
      cancelled = true;
    };
  }, [hcpListRow?.id, commentsRefetchKey]);

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!hcpProfileMenuEnabled || !requestProgressId) {
        if (!cancelled) {
          setProfileSectionComments([]);
          setApprovedProfileSectionKeys([]);
          setProfileExtrasLoaded(true);
        }
        return;
      }
      if (!cancelled) setProfileExtrasLoaded(false);
      try {
        const [commentsRes, approvalRes] = await Promise.all([
          axiosInstance.get(
            `/api/hcp-profile-section-comments/by-progress/enduser/${requestProgressId}`
          ),
          axiosInstance.get(
            `/api/approve-hcp-profile-section/check/enduser/${requestProgressId}`
          ),
        ]);
        if (!cancelled) {
          setProfileSectionComments(commentsRes.data || []);
          setApprovedProfileSectionKeys(
            approvalRes.data?.approved_profile_section_keys || []
          );
        }
      } catch (e) {
        console.error('Error fetching HCP profile section data (customer):', e);
        if (!cancelled) {
          setProfileSectionComments([]);
          setApprovedProfileSectionKeys([]);
        }
      } finally {
        if (!cancelled) setProfileExtrasLoaded(true);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [hcpProfileMenuEnabled, requestProgressId, commentsRefetchKey]);

  React.useEffect(() => {
    if (typeof setComments !== 'function') return;
    const merged = [
      ...(prevComments || []),
      ...(hcpLicensesListComments || []),
      ...(profileSectionComments || []),
    ];
    const seen = new Map();
    merged.forEach((c) => {
      const key = getCommentDedupeKey(c);
      if (!key) return;
      if (!seen.has(key)) seen.set(key, c);
    });
    setComments(Array.from(seen.values()));
  }, [
    prevComments,
    hcpLicensesListComments,
    profileSectionComments,
    setComments,
  ]);

  React.useEffect(() => {
    if (typeof registerSendbackBaseline !== 'function') return;
    if (
      !requestId ||
      loadingComments ||
      !hcpCommentsLoaded ||
      !profileExtrasLoaded
    )
      return;
    const merged = [
      ...(prevComments || []),
      ...(hcpLicensesListComments || []),
      ...(profileSectionComments || []),
    ];
    const keys = merged.map(getCommentDedupeKey).filter(Boolean);
    registerSendbackBaseline(keys);
  }, [
    requestId,
    loadingComments,
    hcpCommentsLoaded,
    profileExtrasLoaded,
    prevComments,
    hcpLicensesListComments,
    profileSectionComments,
    registerSendbackBaseline,
  ]);

  // callback to refetch comments after adding a new one
  const handleAddCommentWrapper = useCallback(
    async (...args) => {
      if (handleAddComment) {
        await handleAddComment(...args);
      }
      // trigger refetch of comments to update icon
      setCommentsRefetchKey((prev) => prev + 1);
    },
    [handleAddComment]
  );

  const handleCommentAdded = useCallback(() => {
    setCommentsRefetchKey((prev) => prev + 1);
  }, []);

  const hcpLicensesListCommentsCount = hcpLicensesListComments.length;

  const customSubForms = React.useMemo(() => {
    const customs = [];
    if (hasHcpLicensesList) {
      customs.push({
        id: 'custom_hcp_licenses_list',
        name: 'License Information',
        isCustom: true,
        approved: isHcpLicensesListApproved ? 1 : 0,
        hasComments: hcpLicensesListCommentsCount > 0,
      });
    }
    return customs;
  }, [
    hasHcpLicensesList,
    isHcpLicensesListApproved,
    hcpLicensesListCommentsCount,
  ]);

  const profileApprovedSet = React.useMemo(
    () => new Set(approvedProfileSectionKeys),
    [approvedProfileSectionKeys]
  );

  const profileCommentCountBySection = React.useMemo(() => {
    const m = new Map();
    for (const c of profileSectionComments || []) {
      const k = c.profile_section_key;
      if (!k) continue;
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [profileSectionComments]);

  const hcpProfileMenuFormEntries = React.useMemo(() => {
    if (
      !hcpProfileMenuEnabled ||
      !Array.isArray(hcpProfileMainMenuTabs) ||
      hcpProfileMainMenuTabs.length === 0
    ) {
      return [];
    }
    return hcpProfileMainMenuTabs.map((tab) => ({
      id: tab.id,
      name: tab.name,
      isHcpProfileMenu: true,
      approved: profileApprovedSet.has(tab.id) ? 1 : 0,
      hasComments: (profileCommentCountBySection.get(tab.id) || 0) > 0,
    }));
  }, [
    hcpProfileMenuEnabled,
    hcpProfileMainMenuTabs,
    profileApprovedSet,
    profileCommentCountBySection,
  ]);

  // License Information first, then HCP profile (when enabled), then workflow subforms
  const allSubForms = React.useMemo(() => {
    return [...customSubForms, ...hcpProfileMenuFormEntries, ...subForms];
  }, [subForms, customSubForms, hcpProfileMenuFormEntries]);

  const [internalActiveTab, setInternalActiveTab] = useActiveTab(
    allSubForms,
    null,
    { preferHcpLicenseTab: hasHcpLicensesList }
  );

  // Parent-controlled vertical tab (MultiFormContext): keep internal state aligned so
  // useActiveTab's items effect does not fight external selection on re-renders.
  React.useEffect(() => {
    if (externalActiveTab == null || externalActiveTab === '') {
      return;
    }
    setInternalActiveTab(externalActiveTab);
  }, [externalActiveTab, setInternalActiveTab]);

  // wrap tab change to sync data before switching
  const wrappedSetInternalActiveTab = React.useCallback(
    async (newTabId) => {
      // syncFormSectionsData reads the getter registered via onFormDataChange(setFormSectionsDataRef).
      // Do not call onFormDataChange() with no args — that would pass undefined into setFormSectionsDataRef and wipe the ref.

      // sync data before tab change if syncFormSectionsData is available
      if (syncFormSectionsData && editable) {
        try {
          await syncFormSectionsData();
        } catch (error) {
          console.error(
            '[FormSectionsCustomer] error syncing form data:',
            error
          );
        }
      }

      setInternalActiveTab(newTabId);
    },
    [syncFormSectionsData, setInternalActiveTab, editable]
  );

  // Use external tab state if provided, otherwise use internal state
  const activeTab = externalActiveTab || internalActiveTab;
  const setActiveTab = externalOnTabChange || wrappedSetInternalActiveTab;

  // Load admin approval state for subform tabs (thumbs-up). skipApprovalFetch only
  // applied to FormSectionsLayout; it must not block this fetch or end users never
  // see section approvals (e.g. New Request / sendback details.js used skip=true).
  const { subForms: subFormsWithApproval } = useSubFormApproval({
    enduserFilledFormId: filledForm?.id ?? null,
    subForms: subForms ?? [],
    persona: 'enduser',
  });

  // License Information, then HCP profile menu (request), then submission form sections
  const allFormsWithApproval = React.useMemo(() => {
    const regularForms =
      subFormsWithApproval.length > 0 ? subFormsWithApproval : subForms;
    return [...customSubForms, ...hcpProfileMenuFormEntries, ...regularForms];
  }, [
    subForms,
    subFormsWithApproval,
    customSubForms,
    hcpProfileMenuFormEntries,
  ]);

  const activeFormForTab = allFormsWithApproval.find((form) =>
    tabIdMatch(form.id, activeTab)
  );
  const isHcpProfileMenuTab = Boolean(activeFormForTab?.isHcpProfileMenu);

  const { requestProgress: currentRequestProgress } = useCurrentStageStatus(
    requestId,
    'enduser'
  );

  const profileSectionAddCommentEnabled =
    enableProfileSectionAddComment != null
      ? enableProfileSectionAddComment
      : enableAddCommentProp;

  const profileCommentProgressId = isSendbackMode
    ? requestProgressId
    : (currentRequestProgress?.id ?? requestProgressId);

  return (
    <FormSectionsLayout
      forms={allFormsWithApproval}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      prevComments={[
        ...(prevComments || []),
        ...(profileSectionComments || []),
      ]}
      requestDetails={requestDetails}
      requestProgressId={requestProgressId}
      persona="enduser"
      filledForm={filledForm}
      requestId={requestId}
      handleAddComment={handleAddCommentWrapper}
      enableAddComment={enableAddCommentProp}
      subformActionButtons={null}
      showRequestBadges={showRequestBadges}
      editable={editable}
      onFormDataChange={onFormDataChange}
      skipApprovalFetch={true}
      formsWithApprovalStatus={allFormsWithApproval}
      errors={errors}
      hasHcpLicensesList={hasHcpLicensesList}
      isSendbackMode={isSendbackMode}
      customFormApprovalProps={{
        onCommentAdded: handleCommentAdded,
      }}
      formActionButtons={null}
      renderHcpProfileMenuContent={
        hcpProfileMenuEnabled &&
        typeof renderHcpProfileMenuContent === 'function'
          ? renderHcpProfileMenuContent
          : null
      }
      hcpProfileReviewerFooter={
        hcpProfileMenuEnabled &&
        isHcpProfileMenuTab &&
        requestProgressId &&
        requestId ? (
          <HcpProfileSectionReviewerPanel
            persona="enduser"
            activeSectionKey={activeTab}
            requestProgressId={requestProgressId}
            commentProgressId={profileCommentProgressId}
            requestId={requestId}
            sectionComments={profileSectionComments}
            onRefresh={async () => {
              setCommentsRefetchKey((k) => k + 1);
            }}
            enableAddComment={profileSectionAddCommentEnabled}
          />
        ) : null
      }
    />
  );
};
